import type { TableSeries } from '@piccolohealth/pbs-common';
import { ChakraV3, FloatingPopover } from '@piccolohealth/ui';
import React from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { FormSection } from '../../../../components/forms/FormSection';
import { HookedSubmitButton } from '../../../../components/forms/V3HookedSubmitButton';
import { zodResolver } from '../../../../utils/zod';
import type { UseTableReturn } from '../../hooks/useTable';
import { tableSeriesSchema } from './TableSeriesAddControl';
import { TableSeriesButtonGroup } from './TableSeriesButtonGroup';
import { TableSeriesForm } from './TableSeriesForm';

type FormValues = TableSeries;

interface TableSeriesEditFormProps {
  table: UseTableReturn;
  series: TableSeries;
  onClose: () => void;
}

export const TableSeriesEditForm = (props: TableSeriesEditFormProps) => {
  const { table, series, onClose } = props;

  const methods = useForm<TableSeries>({
    defaultValues: series,
    resolver: zodResolver(tableSeriesSchema),
  });

  const onSubmit = (values: FormValues) => {
    table.onEditSeries(values.id, values);
    onClose();
  };

  return (
    <ChakraV3.Stack
      bg='white'
      gap={0}
      w='md'
      layerStyle='outline.subtle'
      shadow='2xl'
      rounded='md'
      overflow='hidden'
      p={4}
    >
      <FormProvider {...methods}>
        <FormSection as='form' heading='Edit series' onSubmit={methods.handleSubmit(onSubmit)}>
          <ChakraV3.Separator />
          <TableSeriesForm table={table} />
          <HookedSubmitButton w='full' size='sm'>
            Save
          </HookedSubmitButton>
        </FormSection>
      </FormProvider>
    </ChakraV3.Stack>
  );
};

interface Props {
  table: UseTableReturn;
  series: TableSeries;
}

export const TableSeriesEditControl = (props: Props) => {
  const { series, table } = props;

  const { open, onOpen, onClose } = ChakraV3.useDisclosure();

  return (
    <FloatingPopover
      open={open}
      setOpen={(v) => (v ? onOpen() : onClose())}
      render={() => <TableSeriesEditForm table={table} series={series} onClose={onClose} />}
    >
      <TableSeriesButtonGroup table={table} series={series} onClick={onOpen} />
    </FloatingPopover>
  );
};
